import Alpine from 'alpinejs';

// The login service and Primda behind it go to sleep when idle (mostly in UAT). The first
// request after that can outlast the Shopify app proxy, which then answers with its own
// HTML error page instead of our JSON. A second attempt normally succeeds, so retry
// those cases quietly instead of showing a JSON parse error.
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500;
const REQUEST_TIMEOUT_MS = 30000;
const UNREACHABLE_MESSAGE = 'We could not reach the login service. Please wait a moment and try again.';

type AuthenticateResponse = {
  status?: string;
  code?: string;
  message?: string;
  url?: string;
};

class LoginServiceUnreachable extends Error {}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function postAuthenticate(body: object): Promise<{ response: Response; json: AuthenticateResponse }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${window.location.origin}/apps/acidgreen/authenticate`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    try {
      return { response, json: await response.json() };
    } catch {
      throw new LoginServiceUnreachable();
    }
  } catch (e) {
    // Network failures and timeouts reject the fetch itself.
    throw e instanceof LoginServiceUnreachable ? e : new LoginServiceUnreachable();
  } finally {
    clearTimeout(timer);
  }
}

Alpine.data('SchoolLogin', () => ({
  loading: false,
  errorCode: undefined,
  errorText: '',
  form: {
    code: '',
    password: '',
  },

  handleSubmit: async function (e: Event) {
    e.preventDefault();
    this.loading = true;
    this.errorText = '';
    this.errorCode = undefined;

    const redirect = this.$refs.form.getAttribute('data-redirect');
    const body = {
      username: this.form.code,
      password: this.form.password,
      returnUrl: redirect,
    };

    let result: Awaited<ReturnType<typeof postAuthenticate>> | undefined;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS && !result; attempt++) {
      try {
        result = await postAuthenticate(body);
      } catch {
        if (attempt < MAX_ATTEMPTS) await wait(RETRY_DELAY_MS);
      }
    }

    if (!result) {
      this.errorText = UNREACHABLE_MESSAGE;
      this.loading = false;
      return;
    }

    const { response, json } = result;

    if (json.status === 'success') {
      if (json.url) {
        window.localStorage.setItem('force-school-region', 'true');
        location.href = json.url.replace('unsw-global.myshopify.com', 'shop.icasassessments.com');
        return;
      }
      this.errorText = json.message;
    } else if (response.status == 422 && json.code == 'SHOPIFY_EMAIL_TAKEN') {
      // The template will show a div with text that can be configured in Shopify
      this.errorCode = json.code;
    } else {
      this.errorText = json.message || UNREACHABLE_MESSAGE;
    }
    this.loading = false;
  },
}));
